/**
 * Pure tax calculation engine for RefundRocket.
 *
 * Inputs: filing status + wages + above-the-line deductions.
 * Outputs: AGI, taxable income, federal tax, refund/owed, effective & marginal rate.
 *
 * Design goals:
 *   - Deterministic, side-effect free.
 *   - Numbers everywhere are plain JS numbers in USD (no BigInt needed).
 *   - Edge cases (zero income, negative AGI after deductions, very high
 *     income) all behave sanely and are tested.
 *
 * Non-goals for v1 (deliberately excluded):
 *   - Itemized deductions
 *   - Child Tax Credit (CTC) / EITC
 *   - Capital gains (LTCG) preferential rates
 *   - Self-employment tax / 1099
 *   - State tax
 */

import { FEDERAL_2026 } from '../data/federal-2026';
import type { FilingStatus, TaxBracket } from '../data/federal-2026';
import type { CalcInput } from './schema';
import { roundCents } from './format';

export interface CalcResult {
  /** Adjusted Gross Income. Never negative — clamped to 0. */
  agi: number;
  /** Standard deduction used. */
  standardDeduction: number;
  /** Taxable income = max(0, AGI - standardDeduction). */
  taxableIncome: number;
  /** Federal income tax from brackets. */
  federalTax: number;
  /** Effective rate = federalTax / wages (when wages > 0). */
  effectiveRate: number;
  /** Marginal rate — the rate of the highest bracket the taxpayer hits. */
  marginalRate: number;
  /** Positive = refund, negative = owed. */
  refundOrOwed: number;
  /** Sum of above-the-line deductions applied to AGI. */
  aboveTheLineDeductions: number;
  /** Line-by-line breakdown for UI rendering. */
  breakdown: BreakdownLine[];
}

export interface BreakdownLine {
  label: string;
  amount: number;
  /** Plain "amount", "subtotal", "final". */
  variant?: 'amount' | 'subtotal' | 'final';
}

/**
 * Pick the bracket whose [min, max) interval contains `taxableIncome`.
 * Top bracket has max === null and always matches.
 */
export function selectBracket(
  brackets: readonly TaxBracket[],
  taxableIncome: number,
): TaxBracket {
  if (taxableIncome < 0) {
    // Edge: refund of input — should not happen post-clamp, but be safe.
    const first = brackets[0];
    if (!first) {
      throw new Error('selectBracket: empty bracket table.');
    }
    return first;
  }
  for (const bracket of brackets) {
    const upper = bracket.max ?? Number.POSITIVE_INFINITY;
    if (taxableIncome >= bracket.min && taxableIncome < upper) {
      return bracket;
    }
  }
  // Past the top of the table — return the highest bracket.
  const last = brackets[brackets.length - 1];
  if (!last) {
    throw new Error('selectBracket: empty bracket table.');
  }
  return last;
}

/**
 * Apply progressive brackets to compute total tax on `taxableIncome`.
 * Uses a closed-form per-bracket: tax += (min(max, ti) - bracket.min) * rate
 * clamped to 0 for any bracket whose floor is above ti.
 */
export function taxFromBrackets(
  brackets: readonly TaxBracket[],
  taxableIncome: number,
): number {
  if (taxableIncome <= 0) return 0;
  let total = 0;
  for (const bracket of brackets) {
    if (taxableIncome <= bracket.min) break;
    const upper = bracket.max ?? Number.POSITIVE_INFINITY;
    const span = Math.min(taxableIncome, upper) - bracket.min;
    if (span > 0) {
      total += span * bracket.rate;
    }
  }
  return total;
}

/**
 * Cap student loan interest at the legal maximum (2026: $2,500).
 */
export function capStudentLoanInterest(raw: number): number {
  if (!Number.isFinite(raw) || raw < 0) return 0;
  return Math.min(raw, FEDERAL_2026.studentLoanInterestMax);
}

/**
 * Get the standard deduction for a given filing status.
 */
export function standardDeductionFor(status: FilingStatus): number {
  return FEDERAL_2026.standardDeduction[status];
}

/**
 * Main calculation. Pure function: input -> output.
 *
 * Steps:
 *   1. Sum above-the-line deductions (with student loan interest cap).
 *   2. AGI = wages - aboveTheLine  (clamped to 0).
 *   3. taxableIncome = max(0, AGI - standardDeduction).
 *   4. federalTax = brackets applied to taxableIncome.
 *   5. marginalRate = bracket containing taxableIncome.
 *   6. effectiveRate = federalTax / wages (or 0 if no wages).
 *   7. refundOrOwed = federalWithholding - federalTax  (>0 = refund).
 */
export function calculateRefund(input: CalcInput): CalcResult {
  const filingStatus: FilingStatus = input.filingStatus;
  const brackets = FEDERAL_2026.brackets[filingStatus];
  const deduction = standardDeductionFor(filingStatus);

  const cappedStudentLoan = capStudentLoanInterest(input.studentLoanInterest);
  const aboveTheLine = roundCents(
    Math.max(0, input.retirement401k) +
      Math.max(0, input.hsaContribution) +
      cappedStudentLoan,
  );

  const agiRaw = input.wages - aboveTheLine;
  const agi = roundCents(Math.max(0, agiRaw));

  const taxableIncome = roundCents(Math.max(0, agi - deduction));
  const federalTax = roundCents(taxFromBrackets(brackets, taxableIncome));
  const marginal = selectBracket(brackets, taxableIncome).rate;

  const effectiveRate =
    input.wages > 0 ? federalTax / input.wages : 0;

  const refundOrOwed = roundCents(input.federalWithholding - federalTax);

  const breakdown: BreakdownLine[] = [
    { label: 'W-2 wages (Box 1)', amount: input.wages },
    { label: '401(k) / 403(b) contribution', amount: -Math.max(0, input.retirement401k) },
    { label: 'HSA contribution', amount: -Math.max(0, input.hsaContribution) },
    {
      label: `Student loan interest (cap $${FEDERAL_2026.studentLoanInterestMax.toLocaleString()})`,
      amount: -cappedStudentLoan,
    },
    {
      label: 'Above-the-line deductions',
      amount: -aboveTheLine,
      variant: 'subtotal',
    },
    { label: 'Adjusted Gross Income (AGI)', amount: agi, variant: 'subtotal' },
    { label: `Standard deduction (${labelShort(filingStatus)})`, amount: -deduction },
    { label: 'Taxable income', amount: taxableIncome, variant: 'subtotal' },
    { label: 'Federal income tax', amount: federalTax, variant: 'subtotal' },
    { label: 'Federal withholding', amount: -input.federalWithholding },
    {
      label: refundOrOwed >= 0 ? 'Estimated refund' : 'Estimated amount owed',
      amount: Math.abs(refundOrOwed),
      variant: 'final',
    },
  ];

  return {
    agi,
    standardDeduction: deduction,
    taxableIncome,
    federalTax,
    effectiveRate,
    marginalRate: marginal,
    refundOrOwed,
    aboveTheLineDeductions: aboveTheLine,
    breakdown,
  };
}

function labelShort(status: FilingStatus): string {
  switch (status) {
    case 'single':
      return 'Single';
    case 'mfj':
      return 'MFJ';
    case 'mfs':
      return 'MFS';
    case 'hoh':
      return 'HoH';
  }
}